import $ from 'jquery'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Layer as OLLayer } from 'ol/layer'
import { parseStyle } from '../styleParser'
/**
 * @module
 * @api
 */

/**
 * Layer API class
 * @param {Object} config
 * @constructor
 */
export default function Layer(config) {
  $.extend(this, config || {})

  this.layers = {}

  this.defaultLayerName = 'Default'
}

/**
 * Show layer
 * @param {string} layerName
 * @memberof! module:api/Layer#
 * @api
 */
Layer.prototype.show = function(layerName) {
  var layer = this.getLayerByName(layerName)
  layer.set('visible', true)
  this.toggleToc(layerName, undefined, true)
}

/**
 * Show layer by ID
 * @param {string} layerId
 * @memberof! module:api/Layer#
 * @api
 */
Layer.prototype.showById = function(layerId) {
  var layer = this.getLayerById(layerId)
  layer.set('visible', true)
  this.toggleToc(undefined, layerId, true)
}

/**
 * Hide layer by ID
 * @param {string} layerId
 * @memberof! module:api/Layer#
 * @api
 */
Layer.prototype.hideById = function(layerId) {
  var layer = this.getLayerById(layerId)
  layer.set('visible', false)
  this.toggleToc(undefined, layerId, false)
}

/**
 * Hide layer
 * @param {string} layerName
 * @memberof! module:api/Layer#
 * @api
 */
Layer.prototype.hide = function(layerName) {
  var layer = this.getLayerByName(layerName)
  layer.set('visible', false)
  this.toggleToc(layerName, undefined, false)
}

/**
 * Toggle layer visibility
 * @api
 * @param {string} layerName
 * @memberof! module:api/Layer#
 * @api
 */
Layer.prototype.toggle = function(layerName) {
  var layer = this.getLayerByName(layerName)

  var isVisible = layer.get('visible')
  if (isVisible) {
    this.hide(layerName)
  } else {
    this.show(layerName)
  }
}
Layer.prototype.getConfigLayers = function() {
  return this.config.layers
}

/**
 * Toggle layer visibility by layer Id
 * @api
 * @param {string} layerId
 * @memberof! module:api/Layer#
 * @api
 */
Layer.prototype.toggleById = function(layerId) {
  var layer = this.getLayerById(layerId)

  var isVisible = layer.get('visible')
  if (isVisible) {
    this.hideById(layerId)
  } else {
    this.showById(layerId)
  }
}

/**
 * Toggle layer TOC control
 * @param {string} layerName
 * @param {bool} visible
 * @return {bool} Layer found
 */
Layer.prototype.toggleToc = function(layerName, layerId, visible) {
  if (!this.layerExistsInTOC(layerName, layerId)) {
    return false
  }

  var tocLayer = this.config.layers.filter(function(layer) {
    return (
      layer.name === layerName ||
      (layerId !== undefined && layerId === layer.id)
    )
  })[0]

  tocLayer.visible = visible

  this.toc.ui.render()

  return true
}

/**
 * Get all layers created via API.
 * @api
 * @return {Object} Layers
 * @memberof! module:api/Layer#
 */
Layer.prototype.getLayers = function() {
  return this.layers
}

/**
 * Add Layer
 * @param {string} name
 * @param {object} geoJSON
 * @param {object} style
 * @return {VectorLayer} Created vector layer
 */
Layer.prototype.addLayer = function(name, style) {
  var styleObject = parseStyle(style)
  var vectorSource = new VectorSource()

  var vectorLayer = new VectorLayer({
    source: vectorSource,
    style: styleObject,
    name: name,
    visible: true
  })

  this.layers[name] = vectorLayer
  this.map.addLayer(vectorLayer)

  return vectorLayer
}

/**
 * Remove Layer
 * @param {string} name
 */
Layer.prototype.removeLayer = function(name) {
  this.removeLayerFromToc(name)
  this.map.removeLayer(this.layers[name])
  delete this.layers[name]
}

/**
 * Add layer to TOC
 * @param {OLLayer} layer
 * @param {string} name
 */
Layer.prototype.addLayerToToc = function(layer, name) {
  var layerExists = this.layerExistsInTOC(name)
  if (layerExists) {
    return
  }

  this.config.layers.push({
    mapLayer_: layer,
    name: name,
    index_: this.config.layers.length,
    type: 'Vector',
    visible: layer.getVisible()
  })

  this.toc.ui.render()
}

/**
 * Remove layer from TOC
 * @param {string} name
 */
Layer.prototype.removeLayerFromToc = function(name) {
  var layers = this.config.layers
  var deletedLayers = 0
  for (var key in layers) {
    if (name === layers[key].name) {
      layers[key] = undefined
      delete layers[key]
      deletedLayers++
    }
  }

  if (deletedLayers > 0) {
    this.toc.ui.render()
  }
}

/**
 * Check if layer exists in TOC
 * @param {string} name Layer Name
 * @param {string} id Layer ID
 * @return {bool} returns true if layer exists
 */
Layer.prototype.layerExistsInTOC = function(name, id) {
  var layerExists = false
  this.config.layers.forEach(function(layer) {
    if (name === layer.name || (id !== undefined && id === layer.id)) {
      layerExists = true
    }
  })

  return layerExists
}

/**
 * Gets the dafualt layer name for geometries created via API
 * @return {string} layer name
 */
Layer.prototype.getDefaultLayerName = function() {
  return this.defaultLayerName
}

/**
 * Get layer by name
 * @param {string} layerName
 * @return {olLayer} found layer
 */
Layer.prototype.getLayerByName = function(layerName) {
  var layers = this.map.getLayers()
  var layer = null
  layers.forEach(function(l) {
    if (l.get('name') === layerName) {
      layer = l
    }
  })

  return layer
}

/**
 * Get layer by ID
 * @param {string} layerId
 * @returns {(OLLayer|undefined)} First layer found or undefined if no matching layer exsists
 */
Layer.prototype.getLayerById = function(layerId) {
  var layers = this.map.getLayers().getArray()
  return layers.find(l => l.get('olga_') && l.get('olga_').id === layerId)
}

/**
 * Refresh image layers.
 * @param {string} layerName
 */
 Layer.prototype.refreshLayerByName = function (layerName) {
  var layer = this.getLayerByName(layerName)
  if (!!layer && !layer.getSource().updateParams) return
  layer.getSource().updateParams({ __olgaTS: Date.now() })
}
